import { useCallback, useEffect, useState } from 'react';

/**
 * Fullscreen, which on a phone is not a luxury but the difference between
 * playing and squinting.
 *
 * A Moto g play in landscape is 411 CSS px tall. Chrome's address bar and the
 * system nav take ~80 of them before the app draws a single pixel, and the
 * stake cards, the seat pods and the hint line all pay for it. Asking for
 * fullscreen hands those pixels back.
 *
 * Two things this deliberately does NOT do:
 *
 *  - It does not remember the choice. Fullscreen can only be entered from a
 *    trusted gesture, so a stored `wasFullscreen` could never be honoured on
 *    load — it would just be a preference the app ignores. The button is the
 *    memory.
 *  - It does not pretend on Safari for iPhone, which has never implemented
 *    fullscreen for anything but a <video>. There the button reports itself
 *    unsupported and does not render, rather than sitting there doing nothing.
 */

/** The prefixed spelling, still the only one on Safari before 16.4. */
interface WebkitDocument extends Document {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}
interface WebkitElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}
/**
 * `lock` is spec'd but absent from this TypeScript's lib.dom — the same
 * situation as the prefixed fullscreen names above, so it is declared optional
 * and the call site has to admit it might not be there. `unlock` this lib does
 * know about, and is left alone.
 */
interface LockableOrientation extends ScreenOrientation {
  lock?: (orientation: 'landscape') => Promise<void>;
}
const orientation = (): LockableOrientation | undefined =>
  typeof screen === 'undefined' ? undefined : (screen.orientation as LockableOrientation | undefined);

function fullscreenElement(): Element | null {
  const doc = document as WebkitDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/**
 * `fullscreenEnabled` is the browser answering "would I allow this here?" — it
 * is false inside an iframe that was not given `allow="fullscreen"`, and false
 * on iPhone. That is the question worth asking; the presence of the method is
 * not, because iPhone Safari defines it on <video> and would answer yes.
 */
export function fullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  /*
   * Inside the native app shell the WebView is already every pixel there is —
   * no address bar, no system chrome the API could reclaim. The WebView still
   * answers `fullscreenEnabled: true`, so left alone the button renders and
   * does nothing a player can see. The shell announces itself; believe it.
   */
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (cap?.isNativePlatform?.() === true) return false;
  const doc = document as WebkitDocument;
  return document.fullscreenEnabled === true || doc.webkitFullscreenEnabled === true;
}

/**
 * Landscape, for as long as the browser will allow it.
 *
 * This is the whole of what the web gives: `screen.orientation.lock` is only
 * legal while the document is fullscreen, and it is refused outright on desktop
 * and on iOS. So the lock rides the fullscreen button rather than being asked
 * for on load, where it could only ever be rejected — and the portrait prompt
 * in `global.css` stays as the answer everywhere else, because asking is still
 * all that is on offer there.
 *
 * Rejection is the normal case on half the platforms this runs on, and it is
 * not a failure of anything: fullscreen is still worth having without it.
 */
async function lockLandscape(): Promise<void> {
  await orientation()?.lock?.('landscape').catch(() => undefined);
}

async function enter(): Promise<void> {
  const el = document.documentElement as WebkitElement;
  // `navigationUI: 'hide'` is what buys back the Android system bars; browsers
  // that do not know the option ignore it rather than rejecting.
  if (typeof el.requestFullscreen === 'function') await el.requestFullscreen({ navigationUI: 'hide' });
  else if (typeof el.webkitRequestFullscreen === 'function') await el.webkitRequestFullscreen();
  await lockLandscape();
}

async function exit(): Promise<void> {
  const doc = document as WebkitDocument;
  // Released first: leaving the phone pinned to landscape after the player has
  // asked to come back out is the app overstaying its welcome. Browsers drop
  // the lock with fullscreen anyway; this makes the order deliberate.
  const o = orientation();
  if (typeof o?.unlock === 'function') o.unlock();
  if (typeof document.exitFullscreen === 'function') await document.exitFullscreen();
  else if (typeof doc.webkitExitFullscreen === 'function') await doc.webkitExitFullscreen();
}

export interface Fullscreen {
  /** False on iPhone and in a locked-down iframe — render nothing. */
  supported: boolean;
  active: boolean;
  toggle: () => void;
}

export function useFullscreen(): Fullscreen {
  const [supported] = useState(fullscreenSupported);
  const [active, setActive] = useState(() => fullscreenElement() !== null);

  /*
   * The state is read back from the event, never assumed from the click. Esc,
   * the Android back gesture and the browser's own control all leave fullscreen
   * without going through this button, and an icon that disagrees with the
   * screen is worse than no icon.
   */
  useEffect(() => {
    const sync = (): void => setActive(fullscreenElement() !== null);
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const toggle = useCallback(() => {
    // Rejects when the gesture was not trusted or the user has blocked it.
    // There is nothing to recover and nothing worth saying, but an unhandled
    // rejection in the console is still worse than a swallowed one.
    void (fullscreenElement() === null ? enter() : exit()).catch(() => undefined);
  }, []);

  return { supported, active, toggle };
}
